/**
 * 공통 블록(Site Patterns) — 여러 글에 같은 자리로 붙는 재사용 블록의 순수 규칙.
 *
 * 블록 본문은 ROOT-ADMIN 이 `site_patterns` 에 저장하고, "어느 자리(slot)에 어느
 * 콘텐츠 모델(`model_key`)의 글이면 어떤 블록"인지는 배치 규칙
 * (`settings.site_pattern_placements`)으로 정한다. 유형의 단위는 콘텐츠 모델이다 —
 * 컬렉션 설정(ADR-0060)은 ADR-0105 로 폐기 중이라 규칙 키로 쓰지 않는다. 공개 API 는 글마다 `pattern_slots`(자리 → 블록 key | null) 를 계산해
 * 내려주므로 FRONT 는 규칙을 몰라도 된다. 이 파일은 api-core(계산)·tenant-admin(편집)·
 * cms-renderer-next(렌더)가 같은 판정을 공유하기 위한 프레임워크 비의존 모듈이다.
 *
 * 규칙 판정:
 * - 자리는 `SITE_PATTERN_SLOTS` 에 선언된 것만 계산한다(미선언 자리는 무시).
 * - 글의 `modelKey` 와 같은 규칙이 있으면 그 규칙(블록 없음 = null 도 명시적 결정).
 * - 없으면 `modelKey: null`(모든 글 기본) 규칙.
 * - 가리키는 블록이 발행 상태가 아니면 null(FRONT 가 미발행 블록을 그리지 않게).
 */

/** 블록을 놓을 수 있는 자리. key 는 공개 API `pattern_slots` 의 키와 같다. */
export const SITE_PATTERN_SLOTS = [
  { key: "post_footer", label: "글 하단", description: "글 본문 바로 아래" },
] as const;

export type SitePatternSlotKey = (typeof SITE_PATTERN_SLOTS)[number]["key"];

export const POST_FOOTER_SLOT: SitePatternSlotKey = "post_footer";

/** 배치 규칙을 담는 `settings` 키. */
export const SITE_PATTERN_PLACEMENTS_KEY = "site_pattern_placements";

/** 블록 key 형식 — 소문자·숫자·`-`·`_`, 64자 이하, 영숫자로 시작. */
export const SITE_PATTERN_KEY_PATTERN = /^[a-z0-9][a-z0-9_-]{0,63}$/;

export function isValidSitePatternKey(key: string): boolean {
  return SITE_PATTERN_KEY_PATTERN.test(key);
}

export function isSitePatternSlotKey(value: string): value is SitePatternSlotKey {
  return SITE_PATTERN_SLOTS.some((slot) => slot.key === value);
}

/**
 * 배치 규칙 1건. `modelKey: null` = 모든 글의 기본값, 문자열 = 그 콘텐츠 모델의 글만.
 * `patternKey: null` = 이 자리에 블록을 놓지 않는다(유형 규칙이면 기본값 덮어쓰기).
 */
export interface SitePatternPlacementRule {
  readonly slot: SitePatternSlotKey;
  readonly modelKey: string | null;
  readonly patternKey: string | null;
}

export interface SitePatternPlacements {
  readonly rules: readonly SitePatternPlacementRule[];
}

export const EMPTY_SITE_PATTERN_PLACEMENTS: SitePatternPlacements = { rules: [] };

/** 글 1건의 자리별 블록 key. 선언된 모든 자리를 키로 가진다(없으면 null). */
export type PatternSlots = Record<string, string | null>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readNullableKey(value: unknown): string | null | undefined {
  if (value === null) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  return undefined;
}

/**
 * 저장된 값(unknown) → 정규화된 배치 규칙. 형식이 틀린 항목·미선언 자리는 버리고,
 * 같은 (slot, modelKey) 는 마지막 항목이 이긴다. 이중 인코딩된 JSON 문자열도 받는다.
 * 2026-08-19 이전에 저장된 규칙은 `collectionKey` 필드를 썼다 — `modelKey` 가 없으면 그
 * 값을 읽는다(실측상 남은 규칙은 기본값 `null` 뿐이라 의미가 같다). 다시 저장하면
 * `modelKey` 로 쓰인다.
 */
export function readSitePatternPlacements(raw: unknown): SitePatternPlacements {
  let value = raw;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value) as unknown;
    } catch {
      return EMPTY_SITE_PATTERN_PLACEMENTS;
    }
  }
  if (!isRecord(value) || !Array.isArray(value.rules)) {
    return EMPTY_SITE_PATTERN_PLACEMENTS;
  }
  const byTarget = new Map<string, SitePatternPlacementRule>();
  for (const item of value.rules) {
    if (!isRecord(item)) continue;
    const slot = typeof item.slot === "string" ? item.slot : "";
    if (!isSitePatternSlotKey(slot)) continue;
    const modelKey = readNullableKey(
      item.modelKey === undefined ? item.collectionKey : item.modelKey,
    );
    if (modelKey === undefined) continue;
    const patternKey = readNullableKey(item.patternKey);
    if (patternKey === undefined) continue;
    if (patternKey !== null && !isValidSitePatternKey(patternKey)) continue;
    byTarget.set(`${slot} ${modelKey ?? ""}`, {
      slot,
      modelKey,
      patternKey,
    });
  }
  return { rules: [...byTarget.values()] };
}

/** (slot, modelKey) 에 해당하는 규칙. 없으면 undefined(= 기본값으로 위임). */
export function findSitePatternPlacementRule(
  placements: SitePatternPlacements,
  slot: SitePatternSlotKey,
  modelKey: string | null,
): SitePatternPlacementRule | undefined {
  return placements.rules.find((rule) => rule.slot === slot && rule.modelKey === modelKey);
}

/**
 * 규칙 1건을 갈아 끼운 새 배치 규칙을 돌려준다(불변). `patternKey: undefined` 는
 * "규칙 삭제 = 기본값으로 위임"이다 — 유형 규칙에서 "표시 안 함"(null)과 구분된다.
 */
export function setSitePatternPlacementRule(
  placements: SitePatternPlacements,
  target: { slot: SitePatternSlotKey; modelKey: string | null },
  patternKey: string | null | undefined,
): SitePatternPlacements {
  const rest = placements.rules.filter(
    (rule) => !(rule.slot === target.slot && rule.modelKey === target.modelKey),
  );
  if (patternKey === undefined) return { rules: rest };
  return {
    rules: [...rest, { slot: target.slot, modelKey: target.modelKey, patternKey }],
  };
}

/** 선언된 자리마다 null 인 빈 결과 — 규칙이나 발행 블록이 없을 때의 기본 응답. */
export function emptyPatternSlots(): PatternSlots {
  return Object.fromEntries(SITE_PATTERN_SLOTS.map((slot) => [slot.key, null]));
}

/**
 * 글 1건의 자리별 블록 key 를 계산한다. 글의 콘텐츠 모델(`modelKey`) 규칙 > 모든 글 기본
 * 규칙. `publishedKeys` 에 없는 블록(미발행·삭제)은 null.
 */
export function resolvePatternSlots(
  post: { readonly modelKey: string | null },
  placements: SitePatternPlacements,
  publishedKeys: ReadonlySet<string>,
): PatternSlots {
  const slots = emptyPatternSlots();
  for (const slot of SITE_PATTERN_SLOTS) {
    const specific =
      post.modelKey !== null
        ? findSitePatternPlacementRule(placements, slot.key, post.modelKey)
        : undefined;
    const rule =
      specific ?? findSitePatternPlacementRule(placements, slot.key, null);
    const key = rule?.patternKey ?? null;
    slots[slot.key] = key !== null && publishedKeys.has(key) ? key : null;
  }
  return slots;
}

/**
 * 규칙에서 참조하는 블록 key 집합 — 어드민이 "이 블록은 어디에 쓰이는가"를 표시하거나
 * 삭제 전 경고할 때 쓴다.
 */
export function referencedSitePatternKeys(
  placements: SitePatternPlacements,
): ReadonlySet<string> {
  return new Set(
    placements.rules
      .map((rule) => rule.patternKey)
      .filter((key): key is string => key !== null),
  );
}

// ── 표시 형태(presentation) ──────────────────────────────────────────────────
// 블록의 "어떻게 보일지"는 어드민이 정하고 FRONT 는 값을 받아 그리기만 한다 — 사이트 CSS 에
// 색·모양을 박지 않는다. 값은 작고 범용적인 것만: 카드/본문, 배경색, 링크를 버튼으로, 버튼 색.

export type SitePatternLayout = "plain" | "card";
export type SitePatternLinkStyle = "text" | "buttons";

export interface SitePatternPresentation {
  /** `plain` = 본문처럼 흐름에 놓임, `card` = 배경색이 있는 카드. */
  readonly layout: SitePatternLayout;
  /** 카드 배경색(hex). layout 이 card 일 때만 의미. */
  readonly background: string;
  /** 마지막 문단의 링크를 버튼으로 그릴지. */
  readonly linkStyle: SitePatternLinkStyle;
  /** 버튼 배경색(hex, 순서대로 1·2·3…; 링크가 더 많으면 마지막 색을 반복). 글자는 흰색. */
  readonly buttonColors: readonly string[];
}

export const DEFAULT_SITE_PATTERN_PRESENTATION: SitePatternPresentation = {
  layout: "plain",
  background: "#f8f9fa",
  linkStyle: "text",
  buttonColors: ["#03c75a", "#1a1a1a", "#666666"],
};

export const SITE_PATTERN_BUTTON_COLORS_MAX = 4;

const HEX_COLOR_RE = /^#[0-9a-f]{6}$/;

export function normalizeHexColor(value: unknown): string | null {
  if (typeof value !== "string") return null;
  let hex = value.trim().toLowerCase();
  if (/^#[0-9a-f]{3}$/.test(hex)) hex = `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
  return HEX_COLOR_RE.test(hex) ? hex : null;
}

/** 저장된 값(unknown) → 표시 형태. 틀린 값은 기본값으로 채운다(절대 throw 하지 않음). */
export function readSitePatternPresentation(raw: unknown): SitePatternPresentation {
  let value = raw;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value) as unknown;
    } catch {
      value = null;
    }
  }
  const base = DEFAULT_SITE_PATTERN_PRESENTATION;
  if (!isRecord(value)) return base;
  const layout: SitePatternLayout = value.layout === "card" ? "card" : "plain";
  const linkStyle: SitePatternLinkStyle = value.linkStyle === "buttons" ? "buttons" : "text";
  const background = normalizeHexColor(value.background) ?? base.background;
  const colors = Array.isArray(value.buttonColors)
    ? value.buttonColors
        .map(normalizeHexColor)
        .filter((c): c is string => c !== null)
        .slice(0, SITE_PATTERN_BUTTON_COLORS_MAX)
    : [];
  return {
    layout,
    background,
    linkStyle,
    buttonColors: colors.length > 0 ? colors : base.buttonColors,
  };
}

/** 버튼 n번째(0부터)의 색 — 링크가 색 개수보다 많으면 마지막 색을 반복. */
export function sitePatternButtonColor(presentation: SitePatternPresentation, index: number): string {
  const colors = presentation.buttonColors;
  return colors[Math.min(index, colors.length - 1)] ?? DEFAULT_SITE_PATTERN_PRESENTATION.buttonColors[0]!;
}

/**
 * FRONT 가 래퍼 요소에 얹을 data 속성과 CSS 변수. 사이트 CSS 는 이 변수만 읽는다 —
 * `data-pattern-layout`·`data-pattern-link-style`, `--rt-pattern-bg`, `--rt-pattern-btn-1..n`.
 */
export function sitePatternPresentationAttributes(presentation: SitePatternPresentation): {
  readonly dataset: Readonly<Record<string, string>>;
  readonly cssVariables: Readonly<Record<string, string>>;
} {
  const cssVariables: Record<string, string> = { "--rt-pattern-bg": presentation.background };
  presentation.buttonColors.forEach((color, index) => {
    cssVariables[`--rt-pattern-btn-${index + 1}`] = color;
  });
  return {
    dataset: {
      "data-pattern-layout": presentation.layout,
      "data-pattern-link-style": presentation.linkStyle,
    },
    cssVariables,
  };
}
